import { forwardRef } from "react"
import styled from "styled-components"
import * as Animation from "../../functionality/Animation"
import * as Layout from "../../functionality/Layout"

interface Props {
    widthInPx: number
    tween: number
}

const middleTextScaleValuesHorizontal: Animation.VariableValue = {
    end: 1,
    start: 4,
    endControl: 1,      // Tween
    startControl: 0,    // Tween
}

const middleTextScaleValuesVertical: Animation.VariableValue = {
    end: 1,
    start: 3,
    endControl: 1,      // Tween
    startControl: 0,    // Tween
}

const mainTextOpacityValues: Animation.VariableValue = {
    end: 1,
    start: 0,
    endControl: 1,      // Tween
    startControl: 0,    // Tween
}

const mainTextScaleValues: Animation.VariableValue = {
    end: 1,
    start: 1,
    endControl: 1,      // Tween
    startControl: 0,    // Tween
}

const YourVisionOurExpertise = forwardRef<HTMLDivElement, Props>(({widthInPx, tween}, ref) => {

    const isVertical = Layout.WhatWeDo.Heading.isVerticalForWidthInPx(widthInPx)

    const transformedTween = Animation.easeInOut(tween)

    const mainTextOpacity = Animation.calculateValue(mainTextOpacityValues, tween)
    const middleTextOpacity = tween
    const mainTextScale = Animation.calculateValue(mainTextScaleValues, transformedTween)

    if (isVertical) {

        const heightInPx = Layout.WhatWeDo.Heading.heightWhenVerticalInPx
        const middleTextScale = Animation.calculateValue(middleTextScaleValuesVertical, transformedTween)
        const textSizeInPx = Animation.calculateValue(Layout.WhatWeDo.Heading.verticalTextSize, widthInPx)
        const maxVerticalOffsetInPx = (heightInPx / 3)
        const offset = ((1 - transformedTween) * maxVerticalOffsetInPx)
        const marginBaseInPx = (heightInPx / 6)
        const centerYInPx = (heightInPx / 2)
        const upperTextBottomBaseInPx = (centerYInPx + (textSizeInPx / 2) + marginBaseInPx)
        const middleTextTopInPx = (centerYInPx - (textSizeInPx / 2))
        const bottomTextTopBaseInPx = (centerYInPx + (textSizeInPx / 2) + marginBaseInPx)

        return <Wrapper ref={ref} widthInPx={widthInPx} heightInPx={heightInPx}>
            <Text textSizeInPx={textSizeInPx} opacity={mainTextOpacity} scale={mainTextScale} textAlign="center" baseLeftInPx={0} widthInPx={widthInPx} baseBottomInPx={upperTextBottomBaseInPx} translateYInPx={-offset}>Your vision</Text>
            <Text textSizeInPx={textSizeInPx} opacity={middleTextOpacity} scale={middleTextScale} textAlign="center" baseLeftInPx={0} widthInPx={widthInPx} baseTopInPx={middleTextTopInPx}>+</Text>
            <Text textSizeInPx={textSizeInPx} opacity={mainTextOpacity} scale={mainTextScale} textAlign="center" baseLeftInPx={0} widthInPx={widthInPx} baseTopInPx={bottomTextTopBaseInPx} translateYInPx={offset}>Our expertise</Text>
        </Wrapper>
    }
    else {

        const middleTextScale = Animation.calculateValue(middleTextScaleValuesHorizontal, transformedTween)
        const maxHorizontalOffsetInPx = (widthInPx / 8)
        const offset = ((1 - transformedTween) * maxHorizontalOffsetInPx)
        const textSizeInPx = Animation.calculateValue(Layout.WhatWeDo.Heading.horizontalTextSize, widthInPx)
        const marginBaseInPx = (maxHorizontalOffsetInPx / 2)
        const centerXInPx = (widthInPx / 2)
        const middleTextWidthInPx = textSizeInPx
        const letterSpacingCorrectionInPx = (textSizeInPx * 0.05)
        const leftTextRightBaseInPx = (centerXInPx + (middleTextWidthInPx / 2) + marginBaseInPx - letterSpacingCorrectionInPx)
        const middleTextLeftInPx = (centerXInPx - (middleTextWidthInPx / 2))
        const rightTextLeftBaseInPx = (centerXInPx + (middleTextWidthInPx / 2) + marginBaseInPx)
        
        return <Wrapper ref={ref} widthInPx={widthInPx} heightInPx={textSizeInPx}>
            <Text textSizeInPx={textSizeInPx} opacity={mainTextOpacity} scale={mainTextScale} baseRightInPx={leftTextRightBaseInPx} translateXInPx={-offset} textAlign="right">Your vision</Text>
            <Text textSizeInPx={textSizeInPx} opacity={middleTextOpacity} scale={middleTextScale} baseLeftInPx={middleTextLeftInPx} widthInPx={middleTextWidthInPx} textAlign="center">+</Text>
            <Text textSizeInPx={textSizeInPx} opacity={mainTextOpacity} scale={mainTextScale} baseLeftInPx={rightTextLeftBaseInPx} translateXInPx={offset} textAlign="left">Our expertise</Text>
        </Wrapper>
    }
})

export default YourVisionOurExpertise

const Wrapper = styled.div.attrs<{widthInPx: number, heightInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
        height: p.heightInPx + "px",
    }
}))`
    position: relative;
    overflow: visible;
`

const Text = styled.div.attrs<{textSizeInPx: number, textAlign: "left" | "center" | "right", opacity: number, scale: number, baseLeftInPx?: number, baseRightInPx?: number, baseTopInPx?: number, baseBottomInPx?: number, translateXInPx?: number, translateYInPx?: number, widthInPx?: number}>(p => ({
    style: {
        fontSize: p.textSizeInPx + "px",
        textAlign: p.textAlign,
        opacity: p.opacity,
        transform: `translate(${p.translateXInPx ?? 0}px, ${p.translateYInPx ?? 0}px) scale(${p.scale}) translateZ(0)`,
        left: p.baseLeftInPx ? p.baseLeftInPx + "px" : undefined,
        right: p.baseRightInPx ? p.baseRightInPx + "px" : undefined,
        top: p.baseTopInPx ? p.baseTopInPx + "px" : undefined,
        bottom: p.baseBottomInPx ? p.baseBottomInPx + "px" : undefined,
        width: p.widthInPx ? p.widthInPx + "px" : undefined,
    }
}))`
    position: absolute;
    font-weight: 100;
    letter-spacing: 0.05em;
    white-space: nowrap;
`